import React, { useState, useEffect } from "react";
import StatCard from "./StatCard";
import {
  Users,
  FileText,
  Clock,
  Plus,
  CheckCircle,
  XCircle,
} from "lucide-react";
import AddStudentModal from "./AddStudentModal";
import AddOpportunityModal from "./AddOpportunityModal";
import { format, parseISO } from "date-fns";
import { toast } from "react-toastify";
import { getMergedData, updateStoredData } from "../lib/dataUtils";
import StudentProfileModal from "./StudentProfileModal";

const Overview = () => {
  // State variables for modal visibility
  const [showAddStudentModal, setShowAddStudentModal] = useState(false);
  const [showAddOpportunityModal, setShowAddOpportunityModal] = useState(false);
  const [opportunities, setOpportunities] = useState([]);
  const [students, setStudents] = useState([]);
  // State for all time logs and pending time log approvals
  const [allTimeLogs, setAllTimeLogs] = useState([]);
  const [pendingTimeLogs, setPendingTimeLogs] = useState([]);
  const [selectedLogs, setSelectedLogs] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);

  useEffect(() => {
    const fetchData = () => {
      const mergedData = getMergedData("timeLogs") || [];

      // Sort the mergedData by date in descending order
      mergedData.sort((a, b) => new Date(b.date) - new Date(a.date));

      setAllTimeLogs(mergedData);
      const pendingLogs = mergedData.filter((log) => log.status === "pending");
      setPendingTimeLogs(pendingLogs);

      // Fetch opportunities data
      const opportunitiesData = getMergedData("opportunities") || [];
      setOpportunities(opportunitiesData);

      const studentsData = getMergedData("students") || [];
      setStudents(studentsData);
    };

    fetchData();
  }, []);

  // Handlers for form submissions
  const handleAddStudent = (studentData) => {
    const newStudent = { ...studentData, id: Date.now() };
    const updatedStudents = [...students, newStudent];
    setStudents(updatedStudents);
    updateStoredData("students", updatedStudents);
    console.log("New student added:", newStudent);
    toast.success("Student added successfully!");
    setShowAddStudentModal(false);
  };

  const handleAddOpportunity = (opportunityData) => {
    console.log("New opportunity added:", opportunityData);
    // Update state or send data to backend
  };

  // Handlers for approving/rejecting time logs
  const handleApproveTimeLog = (id) => {
    const updatedTimeLogs = allTimeLogs.map((log) =>
      log.id === id ? { ...log, status: "approved" } : log
    );

    setAllTimeLogs(updatedTimeLogs);
    setPendingTimeLogs(
      updatedTimeLogs.filter((log) => log.status === "pending")
    );
    setSelectedLogs((prevSelected) =>
      prevSelected.filter((logId) => logId !== id)
    );
    updateStoredData("timeLogs", updatedTimeLogs);
    console.log(`Time log ${id} approved`);
  };

  const handleRejectTimeLog = (id) => {
    const updatedTimeLogs = allTimeLogs.map((log) =>
      log.id === id ? { ...log, status: "rejected" } : log
    );

    setAllTimeLogs(updatedTimeLogs);
    setPendingTimeLogs(
      updatedTimeLogs.filter((log) => log.status === "pending")
    );
    setSelectedLogs((prevSelected) =>
      prevSelected.filter((logId) => logId !== id)
    );
    updateStoredData("timeLogs", updatedTimeLogs);
    console.log(`Time log ${id} rejected`);
  };

  // Bulk Approve Selected Logs
  const handleBulkApprove = () => {
    const updatedTimeLogs = allTimeLogs.map((log) =>
      selectedLogs.includes(log.id) ? { ...log, status: "approved" } : log
    );

    setAllTimeLogs(updatedTimeLogs);
    setPendingTimeLogs(
      updatedTimeLogs.filter((log) => log.status === "pending")
    );
    setSelectedLogs([]);
    updateStoredData("timeLogs", updatedTimeLogs);
    console.log("Bulk approve completed");
  };

  // Bulk Reject Selected Logs
  const handleBulkReject = () => {
    const updatedTimeLogs = allTimeLogs.map((log) =>
      selectedLogs.includes(log.id) ? { ...log, status: "rejected" } : log
    );

    setAllTimeLogs(updatedTimeLogs);
    setPendingTimeLogs(
      updatedTimeLogs.filter((log) => log.status === "pending")
    );
    setSelectedLogs([]);
    updateStoredData("timeLogs", updatedTimeLogs);
    console.log("Bulk reject completed");
  };

  // Function to handle selecting/deselecting individual logs
  const handleSelectLog = (id) => {
    if (selectedLogs.includes(id)) {
      setSelectedLogs(selectedLogs.filter((logId) => logId !== id));
    } else {
      setSelectedLogs([...selectedLogs, id]);
    }
  };

  // Function to handle selecting/deselecting all logs
  const handleSelectAllLogs = (e) => {
    if (e.target.checked) {
      const allLogIds = pendingTimeLogs.map((log) => log.id);
      setSelectedLogs(allLogIds);
    } else {
      setSelectedLogs([]);
    }
  };

  // Helper functions
  const getStudentName = (studentId) => {
    const student = students.find((s) => s.studentId === studentId);
    return student
      ? `${student.firstName} ${student.lastName}`
      : "Deleted student";
  };

  const getOpportunityName = (opportunityId) => {
    const opportunity = opportunities.find((o) => o.id === opportunityId);
    return opportunity ? opportunity.hostName : "Unknown";
  };

  const getOpportunityType = (opportunityId) => {
    const opportunity = opportunities.find((o) => o.id === opportunityId);
    return opportunity ? opportunity.opportunityType : "Unknown";
  };

  const handleStudentNameClick = (studentId) => {
    const student = students.find((s) => s.studentId === studentId);
    setSelectedStudent(student);
  };

  const calculateHoursBetween = (startTime, endTime) => {
    const [startHour, startMinute] = startTime.split(":").map(Number);
    const [endHour, endMinute] = endTime.split(":").map(Number);

    return endHour - startHour + (endMinute - startMinute) / 60;
  };

  const totalApprovedHours = () =>
    Math.round(
      allTimeLogs.reduce((total, log) => {
        if (log.status === "approved") {
          return total + calculateHoursBetween(log.startTime, log.endTime);
        }
        return total;
      }, 0)
    );

  return (
    <div className="p-6 min-h-screen">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h2 className="text-3xl font-bold text-gray-800">Overview</h2>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <button
            onClick={() => setShowAddStudentModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-md flex items-center justify-center shadow-md transition duration-300 w-full sm:w-auto"
          >
            <Plus size={20} className="mr-2" />
            Add Student
          </button>
          <button
            onClick={() => setShowAddOpportunityModal(true)}
            className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-md flex items-center justify-center shadow-md transition duration-300 w-full sm:w-auto"
          >
            <Plus size={20} className="mr-2" />
            Add Opportunity
          </button>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Students"
          value={students.length}
          change={5.2}
          icon={Users}
          color="bg-blue-500"
        />
        <StatCard
          title="Active Opportunities"
          value={opportunities.length}
          change={-2.1}
          icon={FileText}
          color="bg-purple-500"
        />
        <StatCard
          title="Total WBL Hours"
          value={totalApprovedHours()}
          change={8.7}
          icon={Clock}
          color="bg-green-500"
        />
        <StatCard
          title="Pending Approvals"
          value={pendingTimeLogs.length}
          change={-15.3}
          icon={Clock}
          color="bg-yellow-500"
        />
      </div>

      {/* Pending Time Log Approvals */}
      <div className="mt-10">
        <h3 className="text-2xl font-semibold text-gray-800 mb-4">
          Pending Time Log Approvals
        </h3>
        {pendingTimeLogs && pendingTimeLogs.length > 0 ? (
          <div className="bg-white rounded-lg shadow-md overflow-hidden border">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      onChange={handleSelectAllLogs}
                      checked={selectedLogs?.length === pendingTimeLogs.length}
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Student Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Host Site
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Opportunity Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Hours
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Approval
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pendingTimeLogs.map((log) => (
                  <tr
                    key={log.id}
                    className={`${
                      selectedLogs.includes(log.id) ? "bg-blue-50" : ""
                    } hover:bg-gray-100 transition duration-300`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedLogs.includes(log.id)}
                        onChange={() => handleSelectLog(log.id)}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => handleStudentNameClick(log.studentId)}
                        className="text-blue-600 hover:text-blue-900 underline"
                      >
                        {getStudentName(log.studentId)}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {getOpportunityName(log.opportunityId)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {getOpportunityType(log.opportunityId)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {format(parseISO(log.date), "MMM dd, yyyy")}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {log.startTime} - {log.endTime}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center space-x-2">
                        <CheckCircle
                          size={20}
                          className="text-green-500 cursor-pointer hover:text-green-600 transition-colors duration-200"
                          onClick={() => handleApproveTimeLog(log.id)}
                        />
                        <XCircle
                          size={20}
                          className="text-red-500 cursor-pointer hover:text-red-600 transition-colors duration-200"
                          onClick={() => handleRejectTimeLog(log.id)}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
                {/* Bulk Action Icons */}
                <tr className="bg-gray-50">
                  <td
                    className="text-gray-400 px-6 py-4 whitespace-nowrap text-sm"
                    colSpan={6}
                  >
                    {selectedLogs?.length || 0} selected
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="flex items-center space-x-2">
                      <CheckCircle
                        size={22}
                        className={`text-green-500 cursor-pointer ${
                          selectedLogs.length === 0
                            ? "opacity-50 cursor-not-allowed"
                            : "hover:text-green-600"
                        } transition-colors duration-200`}
                        onClick={handleBulkApprove}
                      />
                      <XCircle
                        size={22}
                        className={`text-red-500 cursor-pointer ${
                          selectedLogs.length === 0
                            ? "opacity-50 cursor-not-allowed"
                            : "hover:text-red-600"
                        } transition-colors duration-200`}
                        onClick={handleBulkReject}
                      />
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-white p-6 rounded-lg shadow-md">
            <p className="text-gray-600">No pending time log approvals.</p>
          </div>
        )}
      </div>

      {/* Modals */}
      <AddStudentModal
        isOpen={showAddStudentModal}
        onClose={() => setShowAddStudentModal(false)}
        onSubmit={handleAddStudent}
      />

      <AddOpportunityModal
        isOpen={showAddOpportunityModal}
        onClose={() => setShowAddOpportunityModal(false)}
        onSubmit={handleAddOpportunity}
      />

      {/* Add StudentProfileModal */}
      {selectedStudent && (
        <StudentProfileModal
          student={selectedStudent}
          onClose={() => setSelectedStudent(null)}
          opportunities={opportunities}
          timeLogs={allTimeLogs}
        />
      )}
    </div>
  );
};

export default Overview;